"use client"

import React, { useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import Image from "next/image"
import { Pause, Play } from "lucide-react"
import { useIsMounted } from "@/hooks/use-is-mounted"

// Real student reviews captured from our Discord community
const SUCCESS_SCREENSHOTS = [
    { src: "/success_screenshots/1.png", width: 2050, height: 524 },
    { src: "/success_screenshots/2.png", width: 2040, height: 838 },
    { src: "/success_screenshots/3.png", width: 2042, height: 522 },
    { src: "/success_screenshots/4.png", width: 2042, height: 568 },
    { src: "/success_screenshots/5.png", width: 2056, height: 348 },
    { src: "/success_screenshots/6.png", width: 2054, height: 478 },
    { src: "/success_screenshots/7.png", width: 2044, height: 356 },
]

const ScreenshotCard = ({ shot }: { shot: typeof SUCCESS_SCREENSHOTS[0] }) => {
    return (
        <motion.div
            whileHover={{ y: -5 }}
            className="group relative w-[500px] md:w-[640px] shrink-0 mx-4 self-center"
        >
            <div className="relative rounded-2xl border border-white/10 dark:border-white/5 bg-[#313338] overflow-hidden shadow-lg">
                <Image
                    src={shot.src}
                    alt="Student review from the SATBuddy Discord community"
                    width={shot.width}
                    height={shot.height}
                    className="w-full h-auto"
                    sizes="(max-width: 768px) 500px, 640px"
                    quality={85}
                />
            </div>
        </motion.div>
    )
}

export const ReviewsSection = () => {
    // Duplicate screenshots for infinite horizontal scroll effect
    const marqueeItems = [...SUCCESS_SCREENSHOTS, ...SUCCESS_SCREENSHOTS]

    const prefersReducedMotion = useReducedMotion()
    const mounted = useIsMounted()

    // Derived rather than stored, so there is no effect writing state back on
    // mount. The default follows the reader's motion preference — the
    // stylesheet has already stopped the animation for them, this just makes
    // the button say "Play" instead of offering to pause something static —
    // and an explicit press wins from then on. Gated on `mounted` because the
    // server cannot know the preference and both passes have to agree.
    const [override, setOverride] = useState<boolean | null>(null)
    const playing = override ?? !(mounted && prefersReducedMotion)

    return (
        <section className="relative pt-8 pb-16 md:pt-12 md:pb-24 bg-background overflow-hidden">
            {/* Background embellishments */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none -z-10"></div>

            <div className="container mx-auto px-6 md:px-8 text-center mb-16 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tighter text-foreground bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/70">
                        What our users say
                    </h2>
                    <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-medium">
                        Don't just take our word for it. Join students who are crushing their target scores.
                    </p>
                </motion.div>
            </div>

            <div className="relative flex w-full flex-col items-center justify-center overflow-hidden">
                {/* Fade edges - hidden on mobile */}
                <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none hidden md:block"></div>
                <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none hidden md:block"></div>

                <div className="marquee-viewport flex w-full py-8" data-playing={playing}>
                    <div className="marquee-track flex flex-row items-center" data-playing={playing}>
                        {marqueeItems.map((shot, idx) => (
                            <ScreenshotCard key={idx} shot={shot} />
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => setOverride(!playing)}
                    aria-pressed={!playing}
                    className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-muted/50 px-5 text-sm font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                    {playing ? (
                        <Pause className="h-4 w-4" aria-hidden="true" />
                    ) : (
                        <Play className="h-4 w-4" aria-hidden="true" />
                    )}
                    {playing ? "Pause reviews" : "Play reviews"}
                </button>
            </div>
        </section>
    )
}
